import './browser-fixes.ts';
import { aacRate, resampledFor } from './resample.ts';
import { serializeAudioAdds } from './serial-audio.ts';
import {
  AudioSample,
  MediaStreamAudioTrackSource,
  MediaStreamVideoTrackSource,
  Mp4OutputFormat,
  Output,
  Quality,
  StreamTarget,
  WavOutputFormat,
  type AudioCodec,
  type AudioTransformOptions,
  type EncodedPacket,
  type StreamTargetChunk,
  type VideoCodec,
} from 'mediabunny';
import type { SourceKind, TrackFinishRequest } from '../../../shared/protocol.ts';
import type { VideoPreset } from './capability.ts';
import type { ServerClock } from './clock.ts';
import { localNow } from './emitter.ts';
import { firstDelivery, tapOf, videoAgeMs, videoWaitMaxMs, wallClockedAudio } from './track-processor.ts';
import { countSamples, pcmLevels, trackFps, wavFile } from './mp4.ts';
import type { CapturedSource } from './sources.ts';
import type { LocalStorageFiles } from './storage.ts';
import type { RecoveryDraft } from './uploader.ts';

export interface RecorderOptions {
  source: CapturedSource;
  /** Name of the file in local storage. */
  fileName: string;
  storage: LocalStorageFiles;
  clock: ServerClock;
  videoCodec: VideoCodec | null;
  /** From the hardware check: 'quality' unless this browser's encoder stalls in it (Safari). */
  videoLatencyMode?: 'quality' | 'realtime';
  audioCodec: AudioCodec | null;
  video?: VideoPreset;
  /** Microphone embedded into a camera file as a convenience scratch track. */
  embedAudio?: MediaStreamTrack;
  /** 24-bit WAV for unprocessed "studio" mics, 16-bit otherwise. */
  wavBits?: 16 | 24;
  /** Hard mute of this source: write black frames / silence instead of the real media. */
  isHardMuted?: () => boolean;
  /** Hard mute of the mic embedded in a camera file. */
  isEmbedHardMuted?: () => boolean;
  /**
   * Server-clock ms of the take's start. Sync marks fall every SYNC_MARK_EVERY_S from here, so mark k is the same
   * instant in every file of every participant, however late a file started.
   */
  takeStartServerMs?: number;
  onWrite: (position: number, length: number) => void;
}

export interface TailClip {
  sourceId: string;
  kind: SourceKind;
  label: string;
  mime: string;
  data: Uint8Array;
  /** Media time (s) of the clip's first sample within the full recording. */
  mediaStart: number;
  seconds: number;
  /** Local wall-clock ms of the clip's first sample — lets several clips be replayed in sync. */
  wallStartLocalMs: number;
  frameRate?: number;
  peakDb?: number;
  rmsDb?: number;
  audioOnly: boolean;
  /** The source was hard-muted when the clip was cut, so silence/black is expected. */
  hardMuted?: boolean;
}

interface Fragment {
  start: number;
  end: number;
  t: number;
}

/**
 * The file's start is read from the earliest-arriving samples in its first seconds (the least-delayed one is
 * the truest). Measured in media time, not sample count: Safari's audio blocks are 5 ms, Chrome's 10 ms and
 * video frames 33 ms, and start-up is when the page is busiest.
 */
const ANCHOR_WINDOW_S = 3;
/** Spacing of the take-wide sync marks. */
export const SYNC_MARK_EVERY_S = 10;

/**
 * Records one source to one local file:
 *   camera / screen → fragmented MP4 (H.264 where possible, CFR, keyframe every 2 s)
 *   microphone     → WAV (lossless master)
 * Fragmented MP4 means the file is valid up to the last finished fragment even if the
 * browser crashes, can be uploaded while still growing, and the last few seconds can be
 * cut out and replayed without touching the encoder.
 */
export class SourceRecorder {
  readonly o: RecorderOptions;
  readonly format: 'mp4' | 'wav';
  state: 'idle' | 'recording' | 'finalizing' | 'done' | 'error' = 'idle';
  error: string | null = null;
  mime = '';
  /** Bytes confirmed written to local storage (monotonic except final header patches). */
  durableBytes = 0;
  private initEnd = 0;
  private fragments: Fragment[] = [];
  private pendingMoof: { start: number; t: number } | null = null;
  private output: Output | null = null;
  private videoSource: MediaStreamVideoTrackSource | null = null;
  private clonedTracks: MediaStreamTrack[] = [];

  /**
   * Local wall-clock ms of media time 0. Mediabunny defines time 0 as the arrival of the first
   * media chunk (performance.now) and places every later sample relative to it, so reading that
   * instant is exact. If a future version hides it, fall back to the smallest observed
   * (callback time − media time), which is biased late by however long callbacks lag arrival.
   */
  get anchorLocalMs(): number | null {
    const zero = (this.output as unknown as { _firstMediaStreamTimestamp?: number | null } | null)?._firstMediaStreamTimestamp;
    if (typeof zero === 'number') return performance.timeOrigin + zero * 1000 + this.anchorShiftMs;
    return this.observedAnchorMs;
  }
  private observedAnchorMs: number | null = null;
  /** WAVs on the Safari path: where the first sample was really captured, relative to when it was read. */
  private anchorShiftMs = 0;
  private audioShiftMs: number | null = null;
  /**
   * Encoders label their output packets by their own rules: Chrome with the first input sample's time, Safari with
   * the time of the input block that completed the packet (16 ms late with our 256-frame blocks, and different
   * whenever a gap is filled with silence). Our encoder input is one unbroken run of samples from its first
   * timestamp, and every packet holds a fixed number of them, so each packet's true time is known: first input +
   * the samples before it. Packets are written with that time.
   */
  private retimeAudioPackets(output: Output, firstInput: () => number | null) {
    const muxer = (output as unknown as { _muxer?: { addEncodedAudioPacket?: (track: unknown, packet: EncodedPacket, meta: unknown) => unknown } })._muxer;
    const add = muxer?.addEncodedAudioPacket?.bind(muxer);
    if (!muxer || !add) {
      console.warn('[takehouse] muxer internals changed; audio packets keep the encoder\'s timing');
      return;
    }
    let samplesBefore = 0;
    muxer.addEncodedAudioPacket = (track: unknown, packet: EncodedPacket, meta: unknown) => {
      const start = firstInput();
      const duration = packet.duration;
      if (start != null && duration > 0) {
        const expected = start + samplesBefore;
        const off = packet.timestamp - expected;
        this.packetRetimeMaxMs = Math.max(this.packetRetimeMaxMs, Math.abs(off) * 1000);
        samplesBefore += duration;
        // Only a labelling difference is corrected; anything larger means the stream isn't what we think.
        if (Math.abs(off) < 0.1) packet = packet.clone({ timestamp: expected });
      }
      return add(track, packet, meta);
    };
  }
  private packetRetimeMaxMs = 0;

  /** The file's time 0 as performance.now() seconds: when the library read its first frame or block. */
  private mediaZero(): number | null {
    const zero = (this.output as unknown as { _firstMediaStreamTimestamp?: number | null } | null)?._firstMediaStreamTimestamp;
    return typeof zero === 'number' ? zero : null;
  }
  /** Timing diagnostics (Safari path): which clones feed this file, and when the library first read each. */
  private timing: { video?: MediaStreamTrack; audio?: MediaStreamTrack; audioFirstRead?: () => number | null } = {};
  private firstVideoRead: number | null = null;
  private firstAudioPacketTs: number | null = null;
  private firstAudioInputTs: (() => number | null) | null = null;
  /** Sync marks: [media seconds in this file, server ms] at each take-grid instant the file has passed. */
  private syncPoints: [number, number][] = [];
  /** Least-delayed sample since the last mark: its arrival time is the best reading of where the file is. */
  private windowBest: { delta: number; ts: number; local: number } | null = null;
  private nextMarkServerMs: number | null = null;

  videoFrames = 0;
  /**
   * Under heavy load the recording keeps 1 frame in N, evenly, so the encoder keeps up. Left alone, an overloaded
   * encoder makes mediabunny drop frames at random (whenever its queue is full), which looks far worse.
   */
  frameDivisor = 1;
  /** Frames left out on purpose by frameDivisor (not counted as dropped). */
  reducedFrames = 0;
  private frameCounter = 0;
  /** Time from start() to the first encoded video frame – encoder spin-up cost. */
  firstPacketDelayMs: number | null = null;
  encoderConfig: VideoEncoderConfig | null = null;
  lastMediaTime = 0;
  width?: number;
  height?: number;
  sampleRate?: number;
  channels?: number;
  codec?: string;
  private startedAt = 0;
  onError?: (e: Error) => void;

  constructor(o: RecorderOptions) {
    this.o = o;
    this.format = o.source.kind === 'microphone' || o.source.kind === 'system-audio' ? 'wav' : 'mp4';
  }

  get ext() {
    return this.format;
  }

  get fps() {
    return this.o.video?.fps;
  }

  private releaseLock?: () => void;

  /** Builds the encoder pipeline. Call `start()` at the exact moment capture should begin. */
  async prepare() {
    const { source, storage, fileName } = this.o;
    // Holding a lock while recording lets a later page tell "still recording (maybe in another tab)"
    // apart from "orphaned by a crash" – see Uploader.recoverOrphans().
    if (navigator.locks) {
      await new Promise<void>((granted) => {
        void navigator.locks.request(`takehouse:${fileName}`, () => {
          granted();
          return new Promise<void>((release) => (this.releaseLock = release));
        });
      });
    }
    await storage.open(fileName);
    const writable = new WritableStream<StreamTargetChunk>({
      write: async (chunk) => {
        const end = await storage.write(fileName, chunk.position, chunk.data);
        this.durableBytes = Math.max(this.durableBytes, end);
        this.o.onWrite(chunk.position, chunk.data.byteLength);
      },
    });

    if (this.format === 'wav') {
      const output = new Output({
        format: new WavOutputFormat({
          onHeader: (data, position) => {
            this.initEnd = position + data.byteLength;
          },
        }),
        target: new StreamTarget(writable),
      });
      const track = source.track.clone();
      this.clonedTracks.push(track);
      const wavTimeline = continuousAudio(
        (start, end) => this.observe(start, end),
        0,
        this.o.isHardMuted,
        (s) => this.noteAudioGap(s),
        // A WAV is only its samples: rather than move them, move where the file starts to its first block's
        // capture time.
        {
          wav: true,
          zero: () => this.mediaZero(),
          onFirst: (wall) => {
            const zero = this.mediaZero();
            if (zero != null) this.anchorShiftMs = this.audioShiftMs = round1((wall - zero) * 1000);
          },
        },
      );
      this.timing = { audio: track, audioFirstRead: wavTimeline.firstRead };
      const audio = new MediaStreamAudioTrackSource(track as MediaStreamAudioTrack, {
        codec: this.o.wavBits === 24 ? 'pcm-s24' : 'pcm-s16',
        // Observe at arrival (inside the transform), the same reference the MP4 muxer uses to place
        // audio against video; observing later, at the encoder, adds ~45 ms of buffering bias.
        transform: wavTimeline.transform,
        onEncodedSample: (s) => {
          this.sampleRate = s.sampleRate;
          this.channels = s.numberOfChannels;
        },
      });
      serializeAudioAdds(audio, wavTimeline.arrive);
      audio.errorPromise.catch((e) => this.fail(e));
      output.addAudioTrack(audio, { name: source.label });
      this.output = output;
      this.codec = this.o.wavBits === 24 ? 'pcm-s24le' : 'pcm-s16le';
      return;
    }

    const preset = this.o.video!;
    const settings = source.track.getSettings();
    const [w, h] = fitBox(settings.width ?? preset.width, settings.height ?? preset.height, preset.width, preset.height);
    this.width = w;
    this.height = h;
    const output = new Output({
      format: new Mp4OutputFormat({
        fastStart: 'fragmented',
        minimumFragmentDuration: 1,
        onMoov: (data, position) => {
          this.initEnd = position + data.byteLength;
        },
        onMoof: (data, position, timestamp) => {
          this.pendingMoof = { start: position, t: timestamp };
          void data;
        },
        onMdat: (data, position) => {
          if (this.pendingMoof) {
            this.fragments.push({ start: this.pendingMoof.start, end: position + data.byteLength, t: this.pendingMoof.t });
            this.pendingMoof = null;
            // Only the recent past is needed for replays; the file itself keeps everything.
            if (this.fragments.length > 120) this.fragments.splice(0, this.fragments.length - 120);
          }
        },
      }),
      target: new StreamTarget(writable),
    });
    if (!this.o.videoCodec) throw new Error('No video encoder available in this browser');
    const vtrack = source.track.clone();
    this.timing = { video: vtrack };
    this.clonedTracks.push(vtrack);
    const video = new MediaStreamVideoTrackSource(
      vtrack as MediaStreamVideoTrack,
      {
        codec: this.o.videoCodec,
        quality: new Quality({ bitrate: preset.bitrate }),
        keyFrameInterval: 2,
        // Windows get resized and cameras get swapped: keep one fixed frame size and letterbox.
        sizeChangeBehavior: 'contain',
        transform: {
          width: w,
          height: h,
          // Hard mute: same timestamps, black picture – the file keeps its length and sync.
          process: (sample) => {
            this.firstVideoRead ??= sample.timestamp;
            if (this.frameDivisor > 1 && this.frameCounter++ % this.frameDivisor !== 0) {
              sample.close();
              this.reducedFrames++;
              return null;
            }
            if (!this.o.isHardMuted?.()) return sample;
            sample.close();
            return blackFrame(w, h);
          },
        },
        contentHint: source.kind === 'screen' ? 'detail' : 'motion',
        onEncodedSample: (s) => this.observe(s.timestamp, s.timestamp + (s.duration || 1 / preset.fps)),
        onEncodedPacket: () => {
          if (this.videoFrames++ === 0) this.firstPacketDelayMs = localNow() - this.startedAt;
        },
        onEncoderConfig: (c) => {
          // Mediabunny forces 'realtime' for live sources. For a master recording we want 'quality':
          // the encoder must not drop frames, and on macOS realtime H.264 also takes ~2 s to emit its
          // first frame (frames arriving meanwhile are lost). This callback receives the config object
          // before it is used, so adjusting it here takes effect.
          // Safari's H.264 encoder emits nothing in 'quality' mode, so the hardware check decides.
          c.latencyMode = this.o.videoLatencyMode ?? 'quality';
          this.codec = c.codec;
          this.encoderConfig = c;
        },
      },
      { frameRate: preset.fps },
    );
    video.errorPromise.catch((e) => this.fail(e));
    output.addVideoTrack(video, { name: source.label, frameRate: preset.fps } as any);
    this.videoSource = video;

    // A screen's own sound is recorded as a separate WAV (the screen's "sound" source), never in here.
    const audioTrack = this.o.embedAudio;
    if (audioTrack && this.o.audioCodec) {
      const atrack = audioTrack.clone();
      this.clonedTracks.push(atrack);
      // AAC encoders prepend 2112 samples of priming that this fragmented MP4 doesn't signal for
      // trimming, so decoders play it 44 ms late. Shift it back to keep lip sync exact.
      this.timing.audio = atrack;
      // AAC encoders (Chrome's included) only take 44.1 or 48 kHz, and a Bluetooth headset's microphone delivers
      // 16 kHz: such audio is resampled for this scratch copy, or the whole camera file fails on its first block.
      const aac = this.o.audioCodec === 'aac';
      const timeline = continuousAudio(
        undefined,
        aac ? 2112 : 0,
        this.o.isEmbedHardMuted,
        undefined,
        {
          wav: false,
          zero: () => this.mediaZero(),
          onFirst: (wall) => {
            const zero = this.mediaZero();
            const read = timeline.firstRead();
            if (zero != null && read != null) this.audioShiftMs = round1((wall - zero - read) * 1000);
          },
        },
        aac ? aacRate : undefined,
      );
      const audio = new MediaStreamAudioTrackSource(atrack as MediaStreamAudioTrack, {
        codec: this.o.audioCodec,
        quality: new Quality({ bitrate: 160_000 }),
        transform: aac ? resampledFor(timeline.transform, aacRate) : timeline.transform,
        // Diagnostics: the encoder's real labelling lag in this take (first packet vs first input).
        onEncodedPacket: (p: { timestamp: number }) => {
          this.firstAudioPacketTs ??= p.timestamp;
        },
        onEncodedSample: (s) => {
          this.sampleRate = s.sampleRate;
          this.channels = s.numberOfChannels;
        },
      });
      this.timing.audioFirstRead = timeline.firstRead;
      this.firstAudioInputTs = timeline.firstInput;
      serializeAudioAdds(audio, timeline.arrive);
      audio.errorPromise.catch((e) => this.fail(e));
      output.addAudioTrack(audio, { name: 'Mic (scratch)' });
      this.retimeAudioPackets(output, timeline.firstInput);
    }
    this.output = output;
  }

  async start() {
    if (!this.output) throw new Error('prepare() first');
    this.startedAt = localNow();
    this.state = 'recording';
    try {
      await this.output.start();
    } catch (e) {
      this.state = 'error';
      this.error = (e as Error).message;
      throw e;
    }
    // The full codec string is only known after the first encoded frame; never block on it.
    void this.output
      .getMimeType()
      .then((m) => (this.mime = m))
      .catch(() => (this.mime = this.format === 'wav' ? 'audio/wav' : 'video/mp4'));
  }

  /**
   * How long each track's first frame or block waited between reaching the recorder and being read by the
   * library, which places it (and so the whole track) by the read. Only measurable on the Safari path.
   */
  timingReport(): { videoWaitMs?: number; audioWaitMs?: number; audioShiftMs?: number; encoderLagMs?: number; packetRetimeMaxMs?: number; tap?: string; videoQueueMaxMs?: number; videoAgeMs?: { avg: number; max: number } } | null {
    const zero = (this.output as unknown as { _firstMediaStreamTimestamp?: number | null } | null)?._firstMediaStreamTimestamp;
    if (typeof zero !== 'number') return null;
    const out: { videoWaitMs?: number; audioWaitMs?: number; audioShiftMs?: number; encoderLagMs?: number; packetRetimeMaxMs?: number; tap?: string; videoQueueMaxMs?: number; videoAgeMs?: { avg: number; max: number } } = {};
    const v = this.timing.video && firstDelivery.get(this.timing.video);
    if (v != null && this.firstVideoRead != null) out.videoWaitMs = round1(zero * 1000 + this.firstVideoRead * 1000 - v);
    const a = this.timing.audio && firstDelivery.get(this.timing.audio);
    const aRead = this.timing.audioFirstRead?.();
    if (a != null && aRead != null) out.audioWaitMs = round1(zero * 1000 + aRead * 1000 - a);
    if (this.audioShiftMs != null) out.audioShiftMs = this.audioShiftMs;
    const firstIn = this.firstAudioInputTs?.();
    if (this.firstAudioPacketTs != null && firstIn != null) out.encoderLagMs = round1((this.firstAudioPacketTs - firstIn) * 1000);
    if (this.packetRetimeMaxMs) out.packetRetimeMaxMs = round1(this.packetRetimeMaxMs);
    const tap = this.timing.audio && tapOf.get(this.timing.audio);
    if (tap) out.tap = tap;
    const vw = this.timing.video && videoWaitMaxMs.get(this.timing.video);
    if (vw != null) out.videoQueueMaxMs = vw;
    const age = this.timing.video && videoAgeMs.get(this.timing.video);
    if (age?.n) out.videoAgeMs = { avg: Math.round(age.sum / age.n), max: Math.round(age.max) };
    return out.videoWaitMs == null && out.audioWaitMs == null ? null : out;
  }
  private reportTiming() {
    const t = this.timingReport();
    if (t) console.info(`[takehouse] timing ${this.o.source.label} (${this.format}): ${JSON.stringify(t)}`);
  }

  /** Track media-time vs wall-clock so the file can be placed (anchor) and drift measured (sync points). */
  private anchorFrom: number | null = null;
  private firstMediaStart() {
    return this.anchorFrom ?? 0;
  }

  private observe(mediaStart: number, mediaEnd: number) {
    this.anchorFrom ??= mediaStart;
    const now = localNow();
    const delta = now - mediaStart * 1000;
    if (mediaStart - this.firstMediaStart() < ANCHOR_WINDOW_S) {
      if (this.observedAnchorMs == null || delta < this.observedAnchorMs) this.observedAnchorMs = delta;
    }
    if (!this.windowBest || delta < this.windowBest.delta) this.windowBest = { delta, ts: mediaStart, local: now };
    this.markSync(now);
    if (mediaEnd > this.lastMediaTime) this.lastMediaTime = mediaEnd;
  }

  /**
   * Notes where each take-grid instant falls in this file. The best (least-delayed) sample of the last few
   * seconds gives this file's media-time ↔ server-time relation; the mark is that relation read at the grid time.
   * Marks are taken a second after their instant, so samples from either side of it count.
   */
  private markSync(nowLocal: number) {
    const best = this.windowBest;
    if (!best) return;
    const origin = this.o.takeStartServerMs ?? this.o.clock.toServer(this.startedAt);
    const every = SYNC_MARK_EVERY_S * 1000;
    const serverNow = this.o.clock.toServer(nowLocal);
    // The first mark is the first grid instant at or after this file's own start.
    if (this.nextMarkServerMs == null) {
      const zeroServer = this.o.clock.toServer(best.local) - best.ts * 1000;
      this.nextMarkServerMs = origin + Math.max(0, Math.ceil((zeroServer - origin) / every)) * every;
    }
    if (serverNow < this.nextMarkServerMs + 1000) return;
    const zeroServer = this.o.clock.toServer(best.local) - best.ts * 1000;
    for (; this.nextMarkServerMs + 1000 <= serverNow; this.nextMarkServerMs += every) {
      const media = (this.nextMarkServerMs - zeroServer) / 1000;
      if (media >= 0) this.syncPoints.push([round3(media), Math.round(this.nextMarkServerMs)]);
    }
    this.windowBest = null;
  }

  /** Sync marks so far: [media seconds, server ms]. */
  get marks(): readonly [number, number][] {
    return this.syncPoints;
  }

  /** Seconds of microphone audio that never arrived and were written as silence to keep the file in time. */
  audioGapSeconds = 0;
  private gapWarned = 0;
  private noteAudioGap(seconds: number) {
    this.audioGapSeconds += seconds;
    // Say so once per noticeable amount, not for every small hole.
    if (this.audioGapSeconds - this.gapWarned >= 0.5) {
      this.gapWarned = this.audioGapSeconds;
      console.warn(`[takehouse] ${this.o.source.label}: ${this.audioGapSeconds.toFixed(2)} s of audio never arrived so far (busy computer); silence written so the file keeps time`);
    }
  }

  /** Start-up failed or hung: treat as an error without triggering recovery (which would just retry it). */
  markFailed(message: string) {
    if (this.state === 'done' || this.state === 'finalizing') return;
    this.state = 'error';
    this.error = message;
  }

  private fail(e: unknown) {
    if (this.state === 'error' || this.state === 'done') return;
    const err = e instanceof Error ? e : new Error(String(e));
    console.error('Recorder error', this.o.source.label, err);
    this.state = 'error';
    this.error = err.message;
    this.onError?.(err);
  }

  pause() {
    this.videoSource?.pause();
  }

  /** Frames waiting for the video encoder. Mediabunny starts dropping frames at 8, so 4+ means it is falling behind. */
  get encoderBacklog(): number {
    try {
      return (this.videoSource as unknown as { _encoder?: { getQueueSize?: () => number } } | null)?._encoder?.getQueueSize?.() ?? 0;
    } catch {
      return 0;
    }
  }

  async stop(): Promise<void> {
    if (!this.output || this.state === 'done' || this.state === 'finalizing') return;
    const wasError = this.state === 'error';
    this.state = 'finalizing';
    this.reportTiming();
    try {
      // A broken output may never settle its cancel; don't let it hold up the rest of the take.
      if (wasError) await Promise.race([this.output.cancel(), new Promise((r) => setTimeout(r, 3000))]);
      // A source that never delivered anything can leave finalize waiting for ever; the file on disk is
      // what matters, so don't let that hold the upload's finish (and the server's status) hostage.
      else await Promise.race([this.output.finalize(), new Promise((_, reject) => setTimeout(() => reject(new Error('finalize timed out')), 10_000))]);
    } catch (e) {
      // A failed finalize still leaves every completed fragment playable on disk.
      this.error = (e as Error).message;
    }
    for (const t of this.clonedTracks) t.stop();
    const limit = <T>(p: Promise<T>, fallback: T) => Promise.race([p.catch(() => fallback), new Promise<T>((r) => setTimeout(() => r(fallback), 5000))]);
    await limit(this.o.storage.flush(this.o.fileName), null);
    this.durableBytes = await limit(this.o.storage.size(this.o.fileName), this.durableBytes);
    this.state = wasError ? 'error' : 'done';
    this.releaseLock?.();
  }

  /** Facts for the server's manifest. */
  finishInfo(reason: TrackFinishRequest['endedReason']): Omit<TrackFinishRequest, 'size'> {
    const anchorLocal = this.anchorLocalMs ?? this.startedAt;
    const points = [...this.syncPoints];
    const expectedFrames = this.fps ? Math.round(this.lastMediaTime * this.fps) : 0;
    return {
      anchorServerMs: this.o.clock.toServer(anchorLocal),
      anchorUncertaintyMs: Math.round(this.o.clock.uncertaintyMs * 10) / 10,
      durationMs: Math.round(this.lastMediaTime * 1000),
      codec: this.codec,
      width: this.width,
      height: this.height,
      frameRate: this.fps,
      sampleRate: this.sampleRate,
      channels: this.channels,
      droppedFrames: this.fps ? Math.max(0, expectedFrames - this.videoFrames - this.reducedFrames) : undefined,
      endedReason: reason,
      driftPpm: driftPpm(points),
      syncPoints: points,
    };
  }

  /** What a later page needs to repair and place this file if the browser dies mid-recording. */
  recoveryDraft(): RecoveryDraft | null {
    if (this.anchorLocalMs == null) return null;
    const lastFragment = this.fragments.filter((f) => f.end <= this.durableBytes).at(-1);
    return {
      info: this.finishInfo('error'),
      format: this.format,
      initEnd: this.initEnd,
      safeEnd: this.format === 'mp4' ? (lastFragment?.end ?? this.initEnd) : this.durableBytes,
      blockAlign: this.format === 'wav' && this.channels ? this.channels * ((this.o.wavBits ?? 16) / 8) : undefined,
    };
  }

  /**
   * Cuts the most recent `seconds` out of the file *as stored on disk* and returns a standalone
   * clip. Proves end-to-end that capture → encode → mux → disk is working.
   */
  async tailClip(seconds = 5): Promise<TailClip | null> {
    const { storage, fileName, source } = this.o;
    const base = { sourceId: source.id, kind: source.kind, label: source.label, hardMuted: !!this.o.isHardMuted?.() };
    if (this.format === 'wav') {
      if (!this.sampleRate || !this.channels || !this.initEnd) return null;
      const bits = this.o.wavBits === 24 ? 24 : 16;
      const block = this.channels * (bits / 8);
      const bytesPerSec = this.sampleRate * block;
      const dataBytes = this.durableBytes - this.initEnd;
      if (dataBytes <= block) return null;
      const want = Math.min(dataBytes, Math.floor((seconds * bytesPerSec) / block) * block);
      const start = this.initEnd + Math.floor((dataBytes - want) / block) * block;
      const pcm = await storage.read(fileName, start, start + want);
      const mediaStart = (start - this.initEnd) / bytesPerSec;
      return {
        ...base,
        mime: 'audio/wav',
        data: wavFile(pcm, this.sampleRate, this.channels, bits),
        mediaStart,
        seconds: pcm.length / bytesPerSec,
        wallStartLocalMs: (this.anchorLocalMs ?? this.startedAt) + mediaStart * 1000,
        ...pcmLevels(pcm, bits),
        audioOnly: true,
      };
    }

    const frags = this.fragments.filter((f) => f.end <= this.durableBytes);
    if (!frags.length || !this.initEnd) return null;
    // The newest fragment's end time is the start of the one being written now (or "now").
    const lastEnd = this.lastMediaTime;
    let first = frags.length - 1;
    while (first > 0 && lastEnd - frags[first].t < seconds) first--;
    const chosen = frags.slice(first);
    const init = await storage.read(fileName, 0, this.initEnd);
    const body = await storage.read(fileName, chosen[0].start, chosen[chosen.length - 1].end);
    const data = new Uint8Array(init.length + body.length);
    data.set(init);
    data.set(body, init.length);
    const nextStart = this.fragments.find((f) => f.t > chosen[chosen.length - 1].t)?.t ?? lastEnd;
    const clipSeconds = Math.max(0.001, nextStart - chosen[0].t);
    const videoSamples = countSamples(data).get(1) ?? 0;
    return {
      ...base,
      mime: this.mime || (await this.output!.getMimeType().catch(() => 'video/mp4')),
      data,
      mediaStart: chosen[0].t,
      seconds: clipSeconds,
      wallStartLocalMs: (this.anchorLocalMs ?? this.startedAt) + chosen[0].t * 1000,
      frameRate: trackFps(data, 1) ?? Math.round((videoSamples / clipSeconds) * 10) / 10,
      audioOnly: false,
    };
  }
}

const round3 = (x: number) => Math.round(x * 1000) / 1000;

/**
 * Browsers' audio capture timestamps jitter, and under load can even step backwards, which
 * a muxer must reject. Audio is a continuous sample stream, so we timestamp it by sample
 * count (the sound card's own clock) and only jump forward when audio was genuinely lost.
 */
/**
 * Rebuilds audio timestamps from the sample count, so the file has no gaps or overlaps. `arrive` must run as
 * each block arrives from the microphone (before any queueing): the wall-clock reading taken there is what
 * places the file on the take timeline. `transform` runs later, when the block is encoded.
 */
function continuousAudio(
  onSample?: (start: number, end: number) => void,
  primingSamples = 0,
  isHardMuted?: () => boolean,
  onGap?: (seconds: number) => void,
  /**
   * Blocks that carry their true capture time (the Safari path; see wallClockedAudio) are placed by it: in a
   * WAV relative to its first block (the file's start moves instead; see onFirst), in an MP4 relative to the
   * file's time 0 (`zero()`, performance.now() seconds). Other audio is placed by arrival, as the library does.
   */
  clock?: { wav: boolean; zero: () => number | null; onFirst?: (firstWallS: number) => void },
  /** The rate the encoder runs at for a given microphone rate, when the audio is resampled on the way. */
  encoderRate?: (inputRate: number) => number,
) {
  let next: number | null = null;
  /** The first sample's timestamp as the library set it: when it read that sample, relative to the file's time 0. */
  let firstRaw: number | null = null;
  let firstWall: number | null = null;
  /** The first timestamp handed to the encoder (diagnostics). */
  let firstInput: number | null = null;
  /** End of the audio handed to the encoder so far. */
  let writtenEnd: number | null = null;
  const assigned = new WeakMap<AudioSample, number>();
  const arrive = (sample: AudioSample, original: AudioSample = sample) => {
    const duration = sample.numberOfFrames / sample.sampleRate;
    firstRaw ??= sample.timestamp;
    const data = (original as unknown as { _data?: unknown })._data;
    const wall = clock && data instanceof AudioData && wallClockedAudio.has(data) ? data.timestamp / 1e6 : null;
    let ts = sample.timestamp;
    if (wall != null && clock) {
      if (firstWall == null) {
        firstWall = wall;
        clock.onFirst?.(wall);
      }
      ts = clock.wav ? wall - firstWall : wall - (clock.zero() ?? wall);
    }
    // Arrival times jitter, so only a clear jump (50 ms) means audio went missing; capture times are exact, so
    // a dropped block (5 ms) already counts and its silence is put back.
    if (next == null || ts > next + (wall != null ? 0.004 : 0.05)) next = ts;
    assigned.set(sample, next);
    onSample?.(next, next + duration);
    next += duration;
  };
  const transform: AudioTransformOptions = {
    process: (sample) => {
      if (!assigned.has(sample)) arrive(sample);
      // Priming is counted in the encoder's samples, which may be resampled from the microphone's.
      const priming = primingSamples / (encoderRate?.(sample.sampleRate) ?? sample.sampleRate);
      const timestamp = assigned.get(sample)! - priming;
      // Audio captured before the file's time 0 (moved there by the shift) has no place in it. (The AAC priming
      // offset makes the first block slightly negative on purpose; that's allowed.)
      if (timestamp + priming < 0) {
        sample.close();
        return null;
      }
      // Audio that never arrived (a stalled or busy page) must still take up its time: a WAV is just samples in
      // a row, so a missing second would pull everything after it a second early. Fill the hole with silence.
      const gap = writtenEnd == null ? 0 : timestamp - writtenEnd;
      writtenEnd = timestamp + sample.numberOfFrames / sample.sampleRate;
      let filler: AudioSample | null = null;
      if (gap > 0.002) {
        const frames = Math.min(Math.round(gap * sample.sampleRate), sample.sampleRate * 60);
        filler = new AudioSample({
          data: new Float32Array(frames * sample.numberOfChannels),
          format: 'f32-planar',
          numberOfChannels: sample.numberOfChannels,
          sampleRate: sample.sampleRate,
          timestamp: timestamp - frames / sample.sampleRate,
        });
        onGap?.(frames / sample.sampleRate);
      }
      const withFiller = (s: AudioSample) => (filler ? [filler, s] : s);
      if (isHardMuted?.()) {
        // Hard mute: digital silence of exactly the same length, so sync is untouched.
        const silent = new AudioSample({
          data: new Float32Array(sample.numberOfFrames * sample.numberOfChannels),
          format: 'f32-planar',
          numberOfChannels: sample.numberOfChannels,
          sampleRate: sample.sampleRate,
          timestamp,
        });
        sample.close();
        return withFiller(silent);
      }
      firstInput ??= filler ? filler.timestamp : timestamp;
      sample.setTimestamp(timestamp);
      return withFiller(sample);
    },
  };
  return { arrive, transform, firstRead: () => firstRaw, firstInput: () => firstInput };
}

const blackFrames = new Map<string, OffscreenCanvas>();
function blackFrame(w: number, h: number) {
  const key = `${w}x${h}`;
  let c = blackFrames.get(key);
  if (!c) {
    c = new OffscreenCanvas(w, h);
    const g = c.getContext('2d')!;
    g.fillStyle = '#000';
    g.fillRect(0, 0, w, h);
    blackFrames.set(key, c);
  }
  return c;
}

/** Largest even-sized box ≤ (maxW × maxH) with the source's aspect ratio. */
export function fitBox(w: number, h: number, maxW: number, maxH: number): [number, number] {
  // Respect orientation: a portrait source gets a portrait box.
  if (h > w) [maxW, maxH] = [maxH, maxW];
  const scale = Math.min(1, maxW / w, maxH / h);
  const even = (x: number) => Math.max(2, Math.round((x * scale) / 2) * 2);
  return [even(w), even(h)];
}

/** Least-squares slope of server time vs media time, as parts-per-million deviation from 1. */
export function driftPpm(points: [number, number][]): number | undefined {
  if (points.length < 6) return undefined;
  const n = points.length;
  const mx = points.reduce((a, p) => a + p[0], 0) / n;
  const my = points.reduce((a, p) => a + p[1], 0) / n;
  let num = 0;
  let den = 0;
  for (const [x, y] of points) {
    num += (x - mx) * (y - my);
    den += (x - mx) ** 2;
  }
  if (den < 1) return undefined;
  const slope = num / den / 1000; // server ms per media ms
  return Math.round((slope - 1) * 1e6 * 10) / 10;
}

const round1 = (x: number) => Math.round(x * 10) / 10;
